import React, { useState, useMemo, useEffect } from "react";
import { COMPONENT_OUTLINE_BORDER } from "../theme/componentBorders";
import { Modal, Button, Input, Space, Tree, Empty, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";

type Key = React.Key;

const { Search } = Input;

// 树节点接口
export interface TreeNode {
  key: string;
  title: React.ReactNode;
  children?: TreeNode[];
  isLeaf?: boolean;
  [key: string]: any;
}

// 搜索配置接口
export interface SearchConfig {
  enabled?: boolean;
  placeholder?: string;
  onSearch?: (value: string) => void;
}

// 选择配置接口
export interface SelectionConfig {
  enabled?: boolean;
  mode?: "single" | "multiple";
  checkable?: boolean;
  selectedKeys?: Key[];
  checkedKeys?: Key[];
  onSelectionChange?: (selectedKeys: Key[]) => void;
  onCheckChange?: (checkedKeys: Key[]) => void;
}

// 操作按钮配置接口
export interface ActionConfig {
  showConfirm?: boolean;
  showCancel?: boolean;
  showClose?: boolean;
  confirmText?: string;
  cancelText?: string;
  closeText?: string;
  onConfirm?: (selectedKeys: Key[]) => void;
  onCancel?: () => void;
}

// 树形组件配置接口
export interface TreeConfig {
  defaultExpandAll?: boolean;
  showLine?: boolean;
  showIcon?: boolean;
  checkable?: boolean;
  selectable?: boolean;
  loading?: boolean;
}

// 模态框尺寸配置接口
export interface ModalSizeConfig {
  width?: number | string;
  height?: string | number;
  maxWidth?: number | string;
  maxHeight?: string | number;
  minWidth?: number | string;
  minHeight?: string | number;
}

// 主要Props接口
export interface GenericModalTreeProps {
  // 弹窗基本属性
  visible: boolean;
  title: string;
  size?: ModalSizeConfig;
  onClose: () => void;

  // 树形数据
  treeData: TreeNode[];

  // 配置项
  searchConfig?: SearchConfig;
  selectionConfig?: SelectionConfig;
  actionConfig?: ActionConfig;
  treeConfig?: TreeConfig;

  // 自定义样式和属性
  modalProps?: any;
  emptyText?: string;
  loadingText?: string;
}

/**
 * 通用树形视图模态框组件
 * 参考 GenericModalForm 的设计模式，提供高度可配置的树形展示功能
 */
const GenericModalTree: React.FC<GenericModalTreeProps> = ({
  visible,
  title,
  size = {},
  onClose,
  treeData = [],
  searchConfig = {},
  selectionConfig = {},
  actionConfig = {},
  treeConfig = {},
  modalProps = {},
  emptyText = "暂无数据",
  loadingText = "加载中...",
}) => {
  // 默认尺寸配置
  const defaultSize: ModalSizeConfig = {
    width: 600,
    height: "60vh",
    maxWidth: "80vw",
    maxHeight: "80vh",
    minWidth: 400,
    minHeight: "40vh",
  };

  // 合并尺寸配置
  const modalSize = { ...defaultSize, ...size };
  // 默认配置合并
  const {
    enabled: searchEnabled = true,
    placeholder: searchPlaceholder = "搜索...",
    onSearch: onSearchCallback,
  } = searchConfig || {};

  const {
    enabled: selectionEnabled = false,
    mode: selectionMode = "multiple",
    checkable = false,
    selectedKeys: externalSelectedKeys = [],
    checkedKeys: externalCheckedKeys = [],
    onSelectionChange,
    onCheckChange,
  } = selectionConfig || {};

  const {
    showConfirm = true,
    showCancel = true,
    showClose = false,
    confirmText = "确认",
    cancelText = "取消",
    closeText = "关闭",
    onConfirm,
    onCancel,
  } = actionConfig || {};

  const {
    defaultExpandAll = true,
    showLine = { showLeafIcon: false },
    showIcon = true,
    loading = false,
  } = treeConfig || {};

  // 内部状态
  const [searchValue, setSearchValue] = useState<string>("");
  const [expandedKeys, setExpandedKeys] = useState<Key[]>(
    defaultExpandAll ? getAllKeys(treeData) : []
  );
  const [internalSelectedKeys, setInternalSelectedKeys] =
    useState<Key[]>(externalSelectedKeys);
  const [internalCheckedKeys, setInternalCheckedKeys] =
    useState<Key[]>(externalCheckedKeys);
  const [autoExpandParent, setAutoExpandParent] = useState<boolean>(true);

  // 获取所有节点的key（用于默认展开）
  function getAllKeys(nodes: TreeNode[]): Key[] {
    const keys: Key[] = [];
    const traverse = (nodeList: TreeNode[]) => {
      nodeList.forEach((node) => {
        keys.push(node.key);
        if (node.children) {
          traverse(node.children);
        }
      });
    };
    traverse(nodes);
    return keys;
  }

  // 同步外部状态
  useEffect(() => {
    if (selectionEnabled) {
      setInternalSelectedKeys(externalSelectedKeys);
    }
    if (checkable) {
      setInternalCheckedKeys(externalCheckedKeys);
    }
  }, [externalSelectedKeys, externalCheckedKeys, selectionEnabled, checkable]);

  // 重置状态
  useEffect(() => {
    if (visible) {
      setSearchValue("");
      // 根据defaultExpandAll设置展开状态
      if (defaultExpandAll) {
        setExpandedKeys(getAllKeys(treeData));
      } else {
        setExpandedKeys([]);
      }
      setAutoExpandParent(true);
    }
  }, [visible, defaultExpandAll, treeData]);

  // 处理搜索
  const handleSearch = (value: string) => {
    setSearchValue(value);

    if (onSearchCallback) {
      onSearchCallback(value);
    }

    // 自动展开匹配的节点
    if (value) {
      const matchedKeys = new Set<Key>();
      const parentKeysToExpand = new Set<Key>();

      const getMatchedKeys = (nodes: TreeNode[], parent?: TreeNode) => {
        nodes.forEach((node) => {
          const nodeWithParent = { ...node, parent };
          const titleText =
            typeof node.title === "string"
              ? node.title
              : node.title?.toString() || "";

          if (titleText.toLowerCase().includes(value.toLowerCase())) {
            matchedKeys.add(node.key);
            // 添加父节点到展开列表
            let currentParent = parent;
            while (currentParent) {
              parentKeysToExpand.add(currentParent.key);
              currentParent = currentParent.parent;
            }
          }

          if (node.children) {
            getMatchedKeys(node.children, nodeWithParent);
          }
        });
      };

      getMatchedKeys(treeData);
      setExpandedKeys(Array.from(parentKeysToExpand));
      setAutoExpandParent(true);
    } else {
      setExpandedKeys([]);
      setAutoExpandParent(false);
    }
  };

  // 处理节点选择
  const handleSelect = (selectedKeys: Key[], info: any) => {
    if (selectionEnabled && onSelectionChange) {
      const keys =
        selectionMode === "single" ? selectedKeys.slice(-1) : selectedKeys;
      setInternalSelectedKeys(keys);
      onSelectionChange(keys);
    }
  };

  // 处理复选框变化
  const handleCheck = (checkedKeys: any, info: any) => {
    if (checkable && onCheckChange) {
      const keys = Array.isArray(checkedKeys)
        ? checkedKeys
        : checkedKeys.checked || [];
      setInternalCheckedKeys(keys);
      onCheckChange(keys);
    }
  };

  // 处理确认
  const handleConfirm = () => {
    if (onConfirm) {
      const keysToConfirm = checkable
        ? internalCheckedKeys
        : internalSelectedKeys;
      onConfirm(keysToConfirm);
    }
    handleClose();
  };

  // 处理取消
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    handleClose();
  };

  // 处理关闭
  const handleClose = () => {
    // 重置选择状态
    setInternalCheckedKeys([]);
    setInternalSelectedKeys([]);
    setExpandedKeys([]);
    setSearchValue("");
    onClose();
  };

  // 过滤树形数据
  const filteredTreeData = useMemo(() => {
    if (!searchValue) return treeData;

    const filterTree = (nodes: TreeNode[]): TreeNode[] => {
      return nodes.reduce((acc, node) => {
        const titleText =
          typeof node.title === "string"
            ? node.title
            : node.title?.toString() || "";

        const matches = titleText
          .toLowerCase()
          .includes(searchValue.toLowerCase());
        const children = node.children ? filterTree(node.children) : [];

        if (matches || children.length > 0) {
          acc.push({
            ...node,
            children: children.length > 0 ? children : node.children,
          });
        }

        return acc;
      }, [] as TreeNode[]);
    };

    return filterTree(treeData);
  }, [treeData, searchValue]);

  // 渲染操作按钮
  const renderFooter = () => {
    const buttons = [];

    if (showCancel) {
      buttons.push(
        <Button key="cancel" onClick={handleCancel}>
          {cancelText}
        </Button>
      );
    }

    if (showConfirm && selectionEnabled) {
      buttons.push(
        <Button key="confirm" type="primary" onClick={handleConfirm}>
          {confirmText}
        </Button>
      );
    }

    if (showClose && !selectionEnabled) {
      buttons.push(
        <Button key="close" onClick={onClose}>
          {closeText}
        </Button>
      );
    }

    return buttons.length > 0 ? buttons : null;
  };

  // 渲染内容
  const renderContent = () => {
    if (loading) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
            gap: 16,
          }}
        >
          <Spin size="large" />
          <span style={{ color: "#666" }}>{loadingText}</span>
        </div>
      );
    }

    if (!treeData || treeData.length === 0) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <Empty description={emptyText} />
        </div>
      );
    }

    return (
      <Tree
        treeData={filteredTreeData}
        checkable={checkable}
        selectable={selectionEnabled}
        selectedKeys={selectionEnabled ? internalSelectedKeys : undefined}
        checkedKeys={checkable ? internalCheckedKeys : undefined}
        onSelect={selectionEnabled ? handleSelect : undefined}
        onCheck={checkable ? handleCheck : undefined}
        expandedKeys={expandedKeys}
        onExpand={(keys) => {
          setExpandedKeys(keys);
          setAutoExpandParent(false);
        }}
        autoExpandParent={autoExpandParent}
        defaultExpandAll={defaultExpandAll}
        showLine={showLine}
        showIcon={showIcon}
        titleRender={(nodeData) => nodeData.title}
        filterTreeNode={(node) => {
          if (!searchValue) return true;
          const titleText =
            typeof node.title === "string"
              ? node.title
              : node.title?.toString() || "";
          return titleText.toLowerCase().includes(searchValue.toLowerCase());
        }}
      />
    );
  };

  return (
    <Modal
      title={title}
      open={visible}
      width={modalSize.width}
      onCancel={handleClose}
      footer={renderFooter()}
      style={{
        top: 20,
        maxWidth: modalSize.maxWidth,
        minWidth: modalSize.minWidth,
      }}
      styles={{
        body: {
          height: modalSize.height,
          maxHeight: modalSize.maxHeight,
          minHeight: modalSize.minHeight,
          padding: 0,
          display: "flex",
          flexDirection: "column",
        },
      }}
      {...modalProps}
    >
      <div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* 搜索栏 */}
        {searchEnabled && !loading && treeData.length > 0 && (
          <div
            style={{
              padding: "16px 16px 0 16px",
              flexShrink: 0,
            }}
          >
            <Search
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => handleSearch(e.target.value)}
              allowClear
              enterButton
            />
          </div>
        )}

        {/* 树形结构容器 */}
        <div
          style={{
            flex: 1,
            overflow: "hidden",
            padding: treeData.length > 0 ? "16px" : "0",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {treeData.length > 0 && (
            <div
              style={{
                flex: 1,
                overflow: "auto",
                border: COMPONENT_OUTLINE_BORDER,
                borderRadius: "6px",
                padding: "16px",
              }}
            >
              {renderContent()}
            </div>
          )}
          {treeData.length === 0 && renderContent()}
        </div>
      </div>
    </Modal>
  );
};

export default GenericModalTree;
